import React, { useState, useEffect } from 'react';
import { Settings, X, Sun, Moon, Palette } from 'lucide-react';
import FloorManager from './FloorManager';
import '../styles/SceneSettings.css';

/**
 * 3D Scene Settings Panel
 * Controls environment background, floor, and lighting for the 3D gallery view
 */
const SceneSettings = ({ 
  collectionId,
  currentSettings,
  onSettingsChange,
  onFloorUpdate,
  onClose 
}) => {
  const [activeTab, setActiveTab] = useState('environment'); // 'environment' | 'floor'
  const [settings, setSettings] = useState({
    backgroundColor: currentSettings?.backgroundColor || '#000000',
    ambientLightIntensity: currentSettings?.ambientLightIntensity || 0.6,
    directionalLightIntensity: currentSettings?.directionalLightIntensity || 1.0,
    ...currentSettings
  });

  // Environment presets with optimized lighting for each
  const environmentPresets = [
    { 
      id: 'gallery-dark', 
      name: 'Gallery Dark', 
      backgroundColor: '#000000',
      ambientIntensity: 0.6,
      directionalIntensity: 1.0,
      description: 'Classic dark gallery - dramatic lighting',
      icon: '🖤'
    },
    { 
      id: 'gallery-white', 
      name: 'Gallery White', 
      backgroundColor: '#ffffff',
      ambientIntensity: 0.8,
      directionalIntensity: 0.6,
      description: 'Clean white gallery - even lighting',
      icon: '🤍'
    },
    { 
      id: 'gallery-gray', 
      name: 'Neutral Gray', 
      backgroundColor: '#404040',
      ambientIntensity: 0.7,
      directionalIntensity: 0.8,
      description: 'Neutral gray - balanced lighting',
      icon: '🩶'
    },
    { 
      id: 'gallery-warm', 
      name: 'Warm White', 
      backgroundColor: '#f5f0e8',
      ambientIntensity: 0.75,
      directionalIntensity: 0.7,
      description: 'Warm gallery feel',
      icon: '🧡'
    },
    { 
      id: 'gallery-cool', 
      name: 'Cool Gray', 
      backgroundColor: '#e8eef5',
      ambientIntensity: 0.75,
      directionalIntensity: 0.7,
      description: 'Cool modern gallery',
      icon: '💙'
    },
    { 
      id: 'museum', 
      name: 'Museum', 
      backgroundColor: '#1a1a2e',
      ambientIntensity: 0.5,
      directionalIntensity: 1.2,
      description: 'Deep museum atmosphere',
      icon: '🏛️'
    }
  ];

  // Apply a preset
  const applyPreset = (preset) => {
    const newSettings = {
      ...settings,
      backgroundColor: preset.backgroundColor,
      ambientLightIntensity: preset.ambientIntensity,
      directionalLightIntensity: preset.directionalIntensity,
      presetId: preset.id
    };
    setSettings(newSettings);
    onSettingsChange(newSettings);
  };

  // Handle custom color change
  const handleColorChange = (color) => {
    const newSettings = {
      ...settings,
      backgroundColor: color,
      presetId: null // Clear preset when custom color is used
    };
    setSettings(newSettings);
    onSettingsChange(newSettings);
  };

  // Handle lighting change
  const handleLightingChange = (key, value) => {
    const newSettings = {
      ...settings,
      [key]: value,
      presetId: null
    };
    setSettings(newSettings);
    onSettingsChange(newSettings);
  };

  return (
    <div className="scene-settings-overlay">
      <div className="scene-settings-modal">
        <div className="scene-settings-header">
          <div className="header-content">
            <Settings size={20} />
            <h2>3D Scene Settings</h2>
          </div>
          <p>Customize your gallery environment</p>
          <button onClick={onClose} className="close-btn">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="scene-settings-tabs">
          <button 
            className={`tab-btn ${activeTab === 'environment' ? 'active' : ''}`}
            onClick={() => setActiveTab('environment')}
          >
            <Palette size={16} />
            Environment
          </button>
          <button 
            className={`tab-btn ${activeTab === 'floor' ? 'active' : ''}`}
            onClick={() => setActiveTab('floor')}
          >
            <span className="tab-icon">🏠</span>
            Floor
          </button>
        </div>

        <div className="scene-settings-content">
          {activeTab === 'environment' && (
            <>
              {/* Environment Presets */}
              <div className="section">
                <h3>Environment Presets</h3>
                <div className="environment-presets">
                  {environmentPresets.map((preset) => (
                    <button
                      key={preset.id}
                      className={`preset-btn ${settings.presetId === preset.id ? 'active' : ''}`}
                      onClick={() => applyPreset(preset)}
                      style={{ 
                        '--preset-color': preset.backgroundColor,
                        backgroundColor: preset.backgroundColor,
                        color: isLightColor(preset.backgroundColor) ? '#1a1a1a' : '#ffffff'
                      }}
                    >
                      <span className="preset-icon">{preset.icon}</span>
                      <span className="preset-name">{preset.name}</span>
                      <span className="preset-desc">{preset.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Color */}
              <div className="section">
                <h3>Custom Background</h3>
                <div className="custom-color-row">
                  <label>Background Color</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={settings.backgroundColor}
                      onChange={(e) => handleColorChange(e.target.value)}
                      className="color-picker"
                    />
                    <input
                      type="text"
                      value={settings.backgroundColor}
                      onChange={(e) => handleColorChange(e.target.value)}
                      className="color-text"
                      placeholder="#000000"
                    />
                  </div>
                </div>
              </div>

              {/* Lighting Controls */}
              <div className="section">
                <h3>Lighting</h3>
                <div className="lighting-controls">
                  <div className="slider-group">
                    <label>
                      <Sun size={14} />
                      Ambient Light
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={settings.ambientLightIntensity}
                      onChange={(e) => handleLightingChange('ambientLightIntensity', parseFloat(e.target.value))}
                    />
                    <span className="slider-value">{(settings.ambientLightIntensity * 100).toFixed(0)}%</span>
                  </div>
                  <div className="slider-group">
                    <label>
                      <Moon size={14} />
                      Directional Light
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={settings.directionalLightIntensity}
                      onChange={(e) => handleLightingChange('directionalLightIntensity', parseFloat(e.target.value))}
                    />
                    <span className="slider-value">{(settings.directionalLightIntensity * 100).toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'floor' && (
            <div className="floor-tab-content">
              <FloorManager
                collectionId={collectionId}
                onFloorUpdate={onFloorUpdate}
                onClose={() => setActiveTab('environment')}
                embedded={true}
              />
            </div>
          )}
        </div>

        <div className="scene-settings-actions">
          <button onClick={onClose} className="done-btn">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper to determine if a color is light or dark
function isLightColor(color) {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
}

export default SceneSettings;
